import React, { useState, useEffect } from 'react';
import { Users, UserX, UserCog, Key, Ban, UserCheck } from 'lucide-react';
import { FadeLoader } from 'react-spinners';
import { useSettings } from '../hooks/useSettings';
import { AddUserForm } from '../modules/user-management/components/AddUserForm';
import { EditUserForm } from '../modules/user-management/components/EditUserForm';
import DeactivatedAccounts from '../modules/user-management/components/DeactivatedAccounts';
import { PlusOutlined } from '@ant-design/icons';
import { User } from '../modules/user-management/types';
import userService from '../modules/user-management/services/userService';
import '../styles/ModulePage.css';
import '../modules/user-management/styles/UserManagement.css';

export const UserManagementPage: React.FC = () => {
  const { settings } = useSettings();
  // HIDDEN: Dark mode support - Keep code for future reuse
  const isDarkMode = false; // settings.theme === 'dark';
  const [activeTab, setActiveTab] = useState<'users' | 'deactivated'>('users');
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [showEditUserForm, setShowEditUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Refresh users when switching to the active users tab
  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Only fetch active users for the active users tab
      const response = await userService.getUsers(1, 100, { isActive: true });
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserCreated = () => {
    setShowAddUserForm(false);
    setMessage({
      type: 'success',
      text: 'User created successfully! Temporary password has been sent to their email.',
    });
    fetchUsers(); // Refresh the user list (will only show active users)

    // Clear message after 5 seconds
    setTimeout(() => setMessage(null), 5000);
  };

  const handleCancelAddUser = () => {
    setShowAddUserForm(false);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditUserForm(true);
  };

  const handleUserUpdated = () => {
    setShowEditUserForm(false);
    setSelectedUser(null);
    setMessage({ type: 'success', text: 'User updated successfully!' });
    fetchUsers(); // Refresh the user list (will only show active users)

    // Clear message after 5 seconds
    setTimeout(() => setMessage(null), 5000);
  };

  const handleCancelEditUser = () => {
    setShowEditUserForm(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async (userId: string) => {
    if (
      window.confirm(
        'Are you sure you want to deactivate this user? This action can be undone.'
      )
    ) {
      try {
        await userService.deleteUser(userId);
        setMessage({ type: 'success', text: 'User deactivated successfully!' });
        fetchUsers(); // Refresh the user list (will only show active users)

        // Clear message after 5 seconds
        setTimeout(() => setMessage(null), 5000);
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to deactivate user' });
        console.error('Error deleting user:', err);
      }
    }
  };

  const handleReactivateUser = async (userId: string) => {
    try {
      const response = await userService.reactivateUser(userId);
      
      if (response.success && response.data?.newTempPassword) {
        setMessage({ 
          type: 'success', 
          text: `User reactivated successfully! A new temporary password has been sent to their email.` 
        });
      } else {
        setMessage({ type: 'success', text: 'User reactivated successfully!' });
      }
      
      fetchUsers(); // Refresh the user list (will only show active users)

      // Clear message after 5 seconds
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reactivate user' });
      console.error('Error reactivating user:', err);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (
      window.confirm(
        "Are you sure you want to reset this user's password? A new temporary password will be sent to their email."
      )
    ) {
      try {
        await userService.resetUserPassword(userId);
        setMessage({
          type: 'success',
          text: "Password reset successfully! New temporary password has been sent to the user's email.",
        });

        // Clear message after 5 seconds
        setTimeout(() => setMessage(null), 5000);
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to reset password' });
        console.error('Error resetting password:', err);
      }
    }
  };

  const getStatusBadge = (user: User) => {
    const status = userService.getUserStatusBadge(user);
    return (
      <span className={`status-badge ${status.color}`}>{status.text}</span>
    );
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const email = user.email.toLowerCase();
    const role = userService.getRoleDisplayName(user.role).toLowerCase();

    return (
      fullName.includes(searchLower) ||
      email.includes(searchLower) ||
      role.includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className={`module-page ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className="module-container">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '50vh',
              width: '100%',
            }}
          >
            <FadeLoader
              color="#1976D2"
              loading={true}
              height={20}
              width={6}
              radius={3}
              margin={3}
              speedMultiplier={0.8}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`module-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="module-container">
        <h1>User Management</h1>
        <p>Manage system users, roles, and access permissions</p>

        {/* Tab Navigation */}
        <div className="user-management-tabs">
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users className="tab-icon" />
            Active Users
          </button>
          <button
            className={`tab-button ${activeTab === 'deactivated' ? 'active' : ''}`}
            onClick={() => setActiveTab('deactivated')}
          >
            <UserX className="tab-icon" />
            Deactivated Accounts
          </button>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}

        {/* Error Display */}
        {error && <div className="message error">{error}</div>}

        {/* Tab Content */}
        {activeTab === 'users' && (
          <div className={`user-list-container ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className="user-list-header" style={isDarkMode ?{background :'#1e293b'}:{}}>
              <h2>System Users ({filteredUsers.length})</h2>
              <button
                className="btn-primary btn-auto"
                onClick={() => setShowAddUserForm(true)}
              >
                <PlusOutlined style={{marginRight: '8px'}} />
                Add New User
              </button>
            </div>

            <div className="search-section" style={isDarkMode ?{background :'#1e293b'}:{}}>
              <input
                type="text"
                placeholder="Search users by name, email, or role..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {users.length === 0 ? (
              <div className="empty-state">
                <h3>No users found</h3>
                <p>Get started by adding your first user to the system.</p>
                <button
                  className="btn-primary btn-auto"
                  onClick={() => setShowAddUserForm(true)}
                >
                  <span>+</span>
                  Add First User
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="user-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user._id}>
                        <td>
                          <div>
                            <strong>
                              {user.firstName} {user.lastName}
                            </strong>
                            <br />
                            <small>{user.email}</small>
                          </div>
                        </td>
                        <td>
                          <span className="role-badge">
                            {userService.getRoleDisplayName(user.role)}
                          </span>
                        </td>
                        <td>{getStatusBadge(user)}</td>
                        <td>
                          <small>
                            {userService.formatDate(user.createdAt)}
                          </small>
                        </td>
                        <td>
                          <div className="action-buttons">
                            {!user.isActive ? (
                              <button
                                className="action-button reactivate"
                                onClick={() => handleReactivateUser(user._id)}
                                title="Reactivate user"
                              >
                                <UserCheck
                                  className="button-icon"
                                  size={16}
                                  strokeWidth={2}
                                />
                              </button>
                            ) : (
                              <>
                                <button
                                  className="action-button edit"
                                  onClick={() => handleEditUser(user)}
                                  title="Edit user"
                                >
                                  <UserCog
                                    className="button-icon"
                                    size={16}
                                    strokeWidth={2}
                                  />
                                </button>
                                <button
                                  className="action-button reset"
                                  onClick={() => handleResetPassword(user._id)}
                                  title="Reset password"
                                >
                                  <Key
                                    className="button-icon"
                                    size={16}
                                    strokeWidth={2}
                                  />
                                </button>
                                <button
                                  className="action-button delete"
                                  onClick={() => handleDeleteUser(user._id)}
                                  title="Deactivate user"
                                >
                                  <Ban
                                    className="button-icon"
                                    size={16}
                                    strokeWidth={2}
                                  />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Deactivated Accounts Tab */}
        {activeTab === 'deactivated' && (
          <DeactivatedAccounts 
            key="deactivated-accounts" 
            onUserReactivated={fetchUsers}
          />
        )}

        {/* Add User Form Modal */}
        {showAddUserForm && (
          <AddUserForm
            onUserCreated={handleUserCreated}
            onCancel={handleCancelAddUser}
          />
        )}

        {/* Edit User Form Modal */}
        {showEditUserForm && selectedUser && (
          <EditUserForm
            user={selectedUser}
            onUserUpdated={handleUserUpdated}
            onCancel={handleCancelEditUser}
          />
        )}
      </div>
    </div>
  );
};
